import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInitials, getRandomColor } from '../../utils/helpers';
import { useModal } from '../../context/ModalContext';
import {
  Send, Zap, Pencil, Trash2, AlertTriangle, Download, BarChart3, Church,
  Calendar, Hand, MessageSquare, DollarSign, Lightbulb, Search, Eye,
  CheckCircle2, PartyPopper, Save, Loader2, Clipboard, TrendingUp, FileText, Heart
} from 'lucide-react';
import {
  exportToCSV,
  exportToJSON,
  exportToExcel,
  exportToPDF,
  formatForExport,
  getExportFilename
} from '../../utils/exportUtils';
import {
  getEngagementScores,
  getMemberHealthStats,
  getAtRiskMembers,
  getAutomationRules,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  toggleAutomationRule,
  getAtRiskAlerts,
  sendBulkSmsToAtRisk,
  getMemberHealthGroups,
  clearApiMessage,
  clearError,
  clearMemberHealthState,
  selectMemberHealthScores,
  selectMemberHealthStats,
  selectAtRiskMembers,
  selectAtRiskPagination,
  selectAutomationRules,
  selectAtRiskAlerts,
  selectMemberHealthGroups,
  selectMemberHealthLoading,
  selectMemberHealthSubmitting,
  selectMemberHealthApiMessage,
  selectMemberHealthError,
} from '../../store/slices/growth/memberHealthSlice';

const MemberHealth = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Refs for edit rule form
  const editTriggerRef = useRef(null);
  const editActionRef = useRef(null);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ==================== SELECTORS ====================
  const scores = useSelector(selectMemberHealthScores) || [];
  const stats = useSelector(selectMemberHealthStats);
  const atRiskMembers = useSelector(selectAtRiskMembers) || [];
  const atRiskPagination = useSelector(selectAtRiskPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const automationRules = useSelector(selectAutomationRules) || [];
  const alerts = useSelector(selectAtRiskAlerts) || [];
  const groups = useSelector(selectMemberHealthGroups) || [];
  const loading = useSelector(selectMemberHealthLoading);
  const isSubmittingRedux = useSelector(selectMemberHealthSubmitting);
  const apiMessage = useSelector(selectMemberHealthApiMessage);
  const error = useSelector(selectMemberHealthError);

  // Track last toast message
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== EFFECTS ====================

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getEngagementScores({ per_page: 100 }));
        await dispatch(getMemberHealthStats());
        await dispatch(getAtRiskMembers({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm,
          group: groupFilter,
          status: statusFilter
        }));
        await dispatch(getAutomationRules());
        await dispatch(getAtRiskAlerts({ per_page: 10 }));
        await dispatch(getMemberHealthGroups());
      } catch (err) {
        console.error('Error loading member health data:', err);
      }
    };

    loadData();

    return () => {
      dispatch(clearMemberHealthState());
    };
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getAtRiskMembers({
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm,
        group: groupFilter,
        status: statusFilter
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, groupFilter, statusFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    if (!apiMessage || !showToast) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== PAGINATION ====================

  const totalItems = atRiskPagination?.total || 0;
  const totalPages = atRiskPagination?.last_page || 1;
  const currentPageNum = atRiskPagination?.current_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Stats calculation
  const statsData = stats || {
    highly_engaged: 0,
    moderate: 0,
    low: 0,
    at_risk: 0,
    total: 0
  };

  // ==================== SEND SMS ====================

  const handleSendSms = (member) => {
    showToastMessage(`📨 SMS sent to ${member.first_name} ${member.last_name}!`, 'success');
  };

  // ==================== SEND TO ALL ====================

  const handleSendToAll = () => {
    if (atRiskMembers.length === 0) {
      showToastMessage('No at-risk members to send to', 'info');
      return;
    }

    const memberIds = atRiskMembers.map(m => m.id);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px' }} /> Send Re-engagement SMS</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send re-engagement SMS to <strong>{atRiskMembers.length}</strong> at-risk members.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="4" 
              defaultValue={`Hello {FirstName}, we noticed we haven't seen you in a while and we miss you! Is everything okay? We are praying for you. Reply if you need anything. — Grace Chapel`}
              id="bulkMessage"
            />
            <div className="form-hint">Use {'{FirstName}'}, {'{LastName}'}, {'{FullName}'} for personalisation</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              const message = document.getElementById('bulkMessage')?.value?.trim() || '';
              if (!message) {
                showToastMessage('Please enter a message', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const result = await dispatch(sendBulkSmsToAtRisk({
                  member_ids: memberIds,
                  message: message
                })).unwrap();

                if (result.success) {
                  closeModal();
                  showToastMessage(`📨 Re-engagement SMS sent to ${atRiskMembers.length} members!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send SMS', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmittingRedux || isSubmitting ? 'Sending...' : 'Send SMS'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== VIEW MEMBER ====================

  const handleViewMember = (member) => {
    const color = getRandomColor(member.id);
    const statusBadge = member.status === 'Active' ? 'badge-green' : member.status === 'Inactive' ? 'badge-red' : 'badge-amber';
    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown';
    const initials = getInitials(fullName);
    const engagementScore = member.engagement_score || member.score || 0;

    openModal(
      <div>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="member-row-avatar" style={{ background: color, width: '46px', height: '46px', fontSize: '16px' }}>
              {initials}
            </div>
            <div>
              <div className="modal-title">{fullName}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                {member.phone} · {member.group || 'N/A'} · {member.status}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            {[
              ['Engagement Score', `${engagementScore}/100`],
              ['Status', `<span class="badge ${statusBadge}">${member.status}</span>`],
              ['Joined', member.joined || member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'],
              ['Group', member.group || 'N/A'],
              ['Phone', member.phone],
              ['Email', member.email || 'N/A']
            ].map(([l, v]) => (
              <div key={l} style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{l}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }} dangerouslySetInnerHTML={{ __html: v }}></div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => { handleSendSms(member); closeModal(); }}><Send size={14} style={{ verticalAlign: '-2px' }} /> Send SMS</button>
        </div>
      </div>
    );
  };

  // ==================== AUTOMATION RULES ====================

  const handleAddRule = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Zap size={16} style={{ verticalAlign: '-3px' }} /> Add Automation Rule</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Trigger Condition <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" placeholder="e.g., Score drops below 30" id="triggerCondition" />
          </div>
          <div className="form-group">
            <label className="form-label">Action <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" placeholder="e.g., Send pastoral check-in SMS" id="ruleAction" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500' }}>Activate immediately</span>
            <label className="toggle">
              <input type="checkbox" defaultChecked id="ruleActive" />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              const trigger = document.getElementById('triggerCondition')?.value?.trim() || '';
              const action = document.getElementById('ruleAction')?.value?.trim() || '';
              const isActive = document.getElementById('ruleActive')?.checked || false;

              if (!trigger || !action) {
                showToastMessage('Please fill in all fields', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const result = await dispatch(createAutomationRule({
                  trigger_condition: trigger,
                  action: action,
                  is_active: isActive,
                  priority: 1
                })).unwrap();

                if (result.success) {
                  closeModal();
                  await dispatch(getAutomationRules());
                  showToastMessage('⚡ Automation rule added!', 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to add rule', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmittingRedux || isSubmitting ? 'Adding...' : 'Add Rule'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditRule = (rule) => {
    setEditingRuleId(rule.id);

    setTimeout(() => {
      if (editTriggerRef.current) editTriggerRef.current.value = rule.trigger_condition || '';
      if (editActionRef.current) editActionRef.current.value = rule.action || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Automation Rule</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Trigger Condition <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editTriggerRef}
              defaultValue={rule.trigger_condition || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Action <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editActionRef}
              defaultValue={rule.action || ''}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500' }}>Active</span>
            <label className="toggle">
              <input type="checkbox" defaultChecked={rule.is_active !== false} />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              const trigger = editTriggerRef.current?.value?.trim() || '';
              const action = editActionRef.current?.value?.trim() || '';

              if (!trigger || !action) {
                showToastMessage('Please fill in all fields', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const result = await dispatch(updateAutomationRule({
                  id: rule.id,
                  data: {
                    trigger_condition: trigger,
                    action: action,
                    is_active: document.querySelector('.toggle input')?.checked || false
                  }
                })).unwrap();

                if (result.success) {
                  closeModal();
                  await dispatch(getAutomationRules());
                  showToastMessage('✅ Automation rule updated!', 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update rule', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmittingRedux || isSubmitting ? 'Saving...' : (<><Save size={14} style={{ verticalAlign: '-2px' }} /> Save Changes</>)}
          </button>
        </div>
      </div>
    );
  };

  const toggleRule = async (id, active) => {
    try {
      const result = await dispatch(toggleAutomationRule(id)).unwrap();
      if (result.success) {
        await dispatch(getAutomationRules());
        const rule = automationRules.find(r => r.id === id);
        showToastMessage(`Rule ${rule?.trigger_condition?.substring(0, 30) || 'Rule'} ${active ? 'activated' : 'paused'}`, active ? 'success' : 'info');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle rule', 'error');
    }
  };

  const handleDeleteRule = (rule) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Automation Rule</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete this automation rule?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the rule.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteAutomationRule(rule.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getAutomationRules());
                  showToastMessage('🗑️ Automation rule deleted', 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete rule', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Rule
          </button>
        </div>
      </div>
    );
  };

  // ==================== EXPORT ====================

  const openExportModal = () => {
    const data = atRiskMembers.map(m => ({
      'Member Name': `${m.first_name || ''} ${m.last_name || ''}`.trim(),
      'Phone': m.phone,
      'Group': m.group || 'N/A',
      'Score': m.engagement_score || m.score || 0,
      'Status': m.status,
      'Email': m.email || 'N/A'
    }));

    const fields = [
      { key: 'Member Name', label: 'Member Name' },
      { key: 'Phone', label: 'Phone' },
      { key: 'Group', label: 'Group' },
      { key: 'Score', label: 'Engagement Score' },
      { key: 'Status', label: 'Status' },
      { key: 'Email', label: 'Email' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export At-Risk Members</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} at-risk member{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const formattedData = formatForExport(data, fields);
                    const filename = getExportFilename('at_risk_members');
                    
                    switch (format) {
                      case 'CSV':
                        exportToCSV(formattedData, filename);
                        break;
                      case 'JSON':
                        exportToJSON(formattedData, filename);
                        break;
                      case 'Excel':
                        exportToExcel(formattedData, filename);
                        break;
                      case 'PDF':
                        exportToPDF(formattedData, 'At-Risk Members Report', filename);
                        break;
                      default:
                        showToastMessage('Export format not supported', 'error');
                        return;
                    }
                    showToastMessage(`📊 ${data.length} members exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '20px', display: 'inline-flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <Clipboard size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  if (loading && atRiskMembers.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ fontSize: '20px', marginBottom: '8px' }}><Loader2 size={20} /></div>
        <div style={{ color: 'var(--text-light)' }}>Loading member health data...</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Engagement scores help you identify at-risk members before they drift away.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={handleSendToAll} disabled={loading || atRiskMembers.length === 0} title="Send re-engagement SMS to all at-risk members">
            <Send size={16} style={{ verticalAlign: '-3px' }} /> Send to All ({atRiskMembers.length})
          </button>
          <button className="btn btn-ghost btn-sm" onClick={openExportModal} disabled={loading || atRiskMembers.length === 0} title="Export at-risk members">
            <Download size={16} style={{ verticalAlign: '-3px' }} /> Export
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Highly Engaged (80-100)</div>
          <div className="stat-value">{statsData.highly_engaged}</div>
          <div className="stat-change up">{statsData.total > 0 ? Math.round(statsData.highly_engaged / statsData.total * 100) : 0}% of members</div>
          <div className="stat-icon"><Heart size={24} style={{ color: 'var(--green)' }} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Moderate (50-79)</div>
          <div className="stat-value">{statsData.moderate}</div>
          <div className="stat-change up">{statsData.total > 0 ? Math.round(statsData.moderate / statsData.total * 100) : 0}%</div>
          <div className="stat-icon"><Heart size={24} style={{ color: 'var(--blue)' }} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">Low (20-49)</div>
          <div className="stat-value">{statsData.low}</div>
          <div className="stat-change down">{statsData.total > 0 ? Math.round(statsData.low / statsData.total * 100) : 0}%</div>
          <div className="stat-icon"><Heart size={24} style={{ color: 'var(--purple)' }} /></div>
        </div>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">At Risk (0-19)</div>
          <div className="stat-value">{statsData.at_risk}</div>
          <div className="stat-change down">needs attention</div>
          <div className="stat-icon"><AlertTriangle size={24} /></div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title"><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Engagement Score Formula</div>
          </div>
          {[
            { factor: 'Sunday Attendance (last 4 weeks)', weight: '40%', icon: Church },
            { factor: 'SMS Poll Responses', weight: '20%', icon: BarChart3 },
            { factor: 'Event RSVP & Attendance', weight: '15%', icon: Calendar },
            { factor: 'Prayer Requests Submitted', weight: '10%', icon: Hand },
            { factor: 'Inbox Replies Sent', weight: '10%', icon: MessageSquare },
            { factor: 'Giving Participation', weight: '5%', icon: DollarSign }
          ].map(f => (
            <div key={f.factor} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
              <f.icon size={18} />
              <div style={{ flex: 1, fontSize: '13px', color: 'var(--text)' }}>{f.factor}</div>
              <span style={{ fontSize: '13px', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace', color: 'var(--navy)' }}>{f.weight}</span>
            </div>
          ))}
          <div style={{ marginTop: '12px', padding: '12px', background: 'var(--gold-pale)', border: '1px solid #f0dfa0', borderRadius: '8px', fontSize: '12px', color: 'var(--text-mid)' }}>
            <Lightbulb size={14} style={{ verticalAlign: '-2px' }} /> Scores are recalculated every Sunday evening automatically.
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
            <div className="card-title"><AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> At-Risk Members — Score Below 25</div>
            <button
              className="btn btn-gold btn-sm"
              onClick={handleSendToAll}
              disabled={loading || atRiskMembers.length === 0}
              title="Send re-engagement SMS to all at-risk members"
            >
              <Send size={16} style={{ verticalAlign: '-3px' }} /> Send to All ({atRiskMembers.length})
            </button>
          </div>
          
          {/* Filters for at-risk members */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '10px', 
            marginBottom: '12px',
            padding: '10px',
            background: 'var(--cream)',
            borderRadius: '8px'
          }}>
            <div className="search-box" style={{ maxWidth: '200px', flex: '1' }}>
              <span className="search-icon"><Search size={14} /></span>
              <input 
                type="text" 
                placeholder="Search…" 
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                title="Search by name, phone, or group"
              />
            </div>
            <select 
              className="form-select" 
              style={{ width: '130px' }}
              value={groupFilter}
              onChange={(e) => {
                setGroupFilter(e.target.value);
                setCurrentPage(1);
              }}
              title="Filter by group"
              disabled={loading}
            >
              <option value="">All Groups</option>
              {groups.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select 
              className="form-select" 
              style={{ width: '130px' }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              title="Filter by status"
              disabled={loading}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {atRiskMembers.length > 0 ? (
            <>
              {atRiskMembers.map(m => {
                const fullName = `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Unknown';
                const color = getRandomColor(m.id);
                const score = m.engagement_score || m.score || 0;
                const initials = getInitials(fullName);
                
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <div className="member-row-avatar" style={{ background: color, width: '30px', height: '30px', fontSize: '11px' }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>{fullName}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{m.group || 'No group'} · {m.status}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div className="progress-bar" style={{ width: '70px' }}>
                        <div className="progress-fill" style={{ width: `${score}%`, background: score < 25 ? 'var(--red)' : 'var(--amber)' }}></div>
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace', color: score < 25 ? 'var(--red)' : 'var(--amber)' }}>
                        {score}
                      </span>
                    </div>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleSendSms(m)}
                      disabled={!m.phone}
                      title={`Send SMS to ${fullName}`}
                    >
                      <Send size={16} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      onClick={() => handleViewMember(m)}
                      title={`View ${fullName}'s profile`}
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-light)',
                  marginTop: '4px'
                }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Showing {((currentPageNum - 1) * itemsPerPage) + 1} to {Math.min(currentPageNum * itemsPerPage, totalItems)} of {totalItems}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={goToPreviousPage}
                      disabled={currentPageNum === 1 || loading}
                      style={{ 
                        opacity: currentPageNum === 1 ? 0.5 : 1, 
                        cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                      }}
                      title="Previous page"
                    >
                      ←
                    </button>
                    
                    {getPageNumbers().map(page => (
                      <button 
                        key={page}
                        className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => goToPage(page)}
                        disabled={loading}
                        title={`Go to page ${page}`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={goToNextPage}
                      disabled={currentPageNum === totalPages || loading}
                      style={{ 
                        opacity: currentPageNum === totalPages ? 0.5 : 1, 
                        cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                      }}
                      title="Next page"
                    >
                      →
                    </button>
                  </div>

                  <select 
                    className="form-select" 
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    disabled={loading}
                    title="Items per page"
                  >
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state" style={{ padding: '20px' }}>
              <div className="empty-icon"><CheckCircle2 size={48} strokeWidth={1.5} /></div>
              <div className="empty-title">No at-risk members found</div>
              <div className="empty-text">All members are engaged and active! <PartyPopper size={14} style={{ verticalAlign: '-2px' }} /></div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title"><Zap size={16} style={{ verticalAlign: '-3px' }} /> Score-Based Automation Triggers</div>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={handleAddRule}
            disabled={loading}
            title="Add new automation rule"
          >
            + Add Rule
          </button>
        </div>
        {automationRules.length > 0 ? (
          automationRules.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0', borderBottom: '1px solid var(--border-light)', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>IF: {r.trigger_condition}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '2px' }}>THEN: {r.action}</div>
              </div>
              <label className="toggle" title={r.is_active ? 'Click to disable' : 'Click to enable'}>
                <input 
                  type="checkbox" 
                  checked={r.is_active !== false} 
                  onChange={(e) => toggleRule(r.id, e.target.checked)}
                  disabled={loading}
                />
                <span className="toggle-slider"></span>
              </label>
              <button 
                className="btn btn-ghost btn-sm btn-icon" 
                onClick={() => handleEditRule(r)}
                disabled={loading}
                title="Edit rule"
              >
                <Pencil size={16} />
              </button>
              <button
                className="btn btn-ghost btn-sm btn-icon"
                onClick={() => handleDeleteRule(r)}
                disabled={loading}
                title="Delete rule"
                style={{ color: 'var(--red)' }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
            No automation rules configured yet.
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .progress-bar {
          width: 100%;
          height: 6px;
          background: var(--cream);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-icon {
          padding: 4px 6px;
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .stat-card {
          padding: 16px;
          border-radius: 12px;
          background: var(--white);
          border: 1px solid var(--border);
          position: relative;
          overflow: hidden;
          transition: all 0.2s;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow);
        }
        .stat-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-light);
          margin-bottom: 4px;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
        }
        .stat-icon {
          position: absolute;
          right: 16px;
          top: 16px;
          font-size: 24px;
          opacity: 0.3;
        }
        .stat-card.gold .stat-value {
          color: var(--gold);
        }
        .stat-card.green .stat-value {
          color: var(--green);
        }
        .stat-card.blue .stat-value {
          color: var(--blue);
        }
        .stat-card.purple .stat-value {
          color: var(--purple);
        }
        .card {
          background: var(--white);
          border-radius: var(--radius);
          border: 1px solid var(--border);
          padding: 16px;
          box-shadow: var(--shadow);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .card-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--navy);
        }
        .search-box {
          display: flex;
          align-items: center;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 4px 12px;
          transition: all 0.2s;
        }
        .search-box:focus-within {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .search-icon {
          font-size: 14px;
          color: var(--text-light);
          margin-right: 8px;
        }
        .search-box input {
          border: none;
          outline: none;
          padding: 8px 0;
          flex: 1;
          font-size: 13px;
          background: transparent;
        }
        .form-select {
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          background: var(--white);
          color: var(--text);
          outline: none;
          transition: all 0.2s;
        }
        .form-select:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .form-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          background: var(--white);
          color: var(--text);
          outline: none;
          transition: all 0.2s;
        }
        .form-input:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .form-textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          background: var(--white);
          color: var(--text);
          outline: none;
          resize: vertical;
          font-family: inherit;
          transition: all 0.2s;
        }
        .form-textarea:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 4px;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          border: 1px solid transparent;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          font-family: inherit;
        }
        .btn-ghost {
          background: transparent;
          color: var(--text-mid);
          border-color: var(--border);
        }
        .btn-ghost:hover {
          background: var(--cream);
        }
        .btn-gold {
          background: var(--gold);
          color: var(--navy);
          border-color: var(--gold);
        }
        .btn-gold:hover {
          background: var(--gold-dark);
        }
        .btn-danger {
          background: var(--red);
          color: white;
          border-color: var(--red);
        }
        .btn-danger:hover {
          background: var(--red-dark);
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-sm.btn-icon {
          padding: 4px 6px;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }
        .empty-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }
        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 4px;
        }
        .empty-text {
          font-size: 13px;
          color: var(--text-light);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border);
        }
        .modal-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--navy);
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: var(--text-light);
          cursor: pointer;
          padding: 0 8px;
        }
        .modal-close:hover {
          color: var(--red);
        }
        .modal-body {
          padding: 16px 24px;
          max-height: 70vh;
          overflow-y: auto;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 24px;
          border-top: 1px solid var(--border);
        }
      `}</style>
    </>
  );
};

export default MemberHealth;